import { format, isAfter, parseISO } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Breakpoints, Grid } from '../../app/StyleGuide';
import ErrorMessage from '../../components/ErrorMessage';
import {
  CollectionImgContainer,
  CollectionImgPlaceholder,
  Container,
  FullWidthCard,
  Image,
  InlineList,
} from '../../components/Layout';
import { ListItemFlexRow } from '../../components/ListItemFlexRow';
import LoadingState from '../../components/LoadingState';
import ProfileImageList from '../../components/ProfileImageList';
import useCurriculum from '../../hooks/useCurriculum';
import curriculumService from '../../services/curriculumService';
import { useUser } from '../authentication';
import { AtHomeBanner } from './components/AtHomeBanner';
import { HeaderNavigation } from './components/HeaderNavigation';

const Collections = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0 0 ${Grid._5};
`;

const CollectionInfo = styled.div`
  display: flex;
  flex-direction: column;
  margin: ${Grid._5} 0;
  h3,
  ul {
    margin-bottom: ${Grid._3};
  }
  p {
    margin: 0;
  }
  @media screen and (min-width: ${Breakpoints.screen_sm}) {
    margin: 0 ${Grid._5};
  }
  .profile-images {
    margin: 0 0 ${Grid._3} ${Grid._3};
  }
`;

const FullWidthCardContainer = styled(FullWidthCard)`
  padding-top: ${Grid._5};
`;

type Collection = {
  collectionId: string;
  name: string;
  startDate: string;
  endDate: string;
  image: string;
};

type Group = {
  memberCount: number;
  profileImages: string[];
};

export function ManageBibleStudyPeople() {
  const { user } = useUser();
  const [data, setData] = useState<{
    isLoading: boolean;
    isError?: boolean;
    collections?: {
      collection: Collection;
      group: Group;
      hasAccess: boolean;
    }[];
  }>({ isLoading: true });
  const { brand, ageCategory } = useCurriculum();
  const { curriculumId } = useParams<{ curriculumId: string }>();

  const crumbs = [{ name: 'Curriculum', route: '#/bible-studies' }, { name: `${brand.name}: ${ageCategory}` }];
  const today = new Date();
  const isAfterToday = (date: string) => isAfter(parseISO(date), today);

  useEffect(() => {
    curriculumService
      .getCollections(curriculumId)
      .then((collections: any[]) => setData({ collections: collections, isLoading: false }))
      .catch((e: Error) => {
        setData({ isLoading: false, isError: true });
        console.error(e.message);
      });
  }, []);

  const redirectToCollection = (collectionId: string) => {
    window.location.href = `#/manage-bible-study/people/collection/${brand.code}/${ageCategory}/${curriculumId}/${collectionId}`;
  };

  return (
    <>
      <HeaderNavigation
        crumbs={crumbs}
        brandCode={brand.code}
        ageCategory={ageCategory}
        curriculumId={curriculumId}
        orgId={user?.lastSelectedAccount}
        activeTab="people"
      />
      <FullWidthCardContainer>
        <Container data-qa-hook="collectionsViewContainer">
          {data.isLoading ? (
            <LoadingState />
          ) : data.isError ? (
            <ErrorMessage>
              A problem occurred showing this page. Please refresh the page to try again.{' '}
              <a href="#/help">Contact Us</a>
            </ErrorMessage>
          ) : (
            <>
              <Collections data-qa-hook="collectionsContainer">
                {data.collections?.map(({ collection, group, hasAccess }, i) => {
                  const totalMembers = group.memberCount;
                  const members =
                    totalMembers === 1 ? '1 person' : totalMembers > 1 ? totalMembers + ' people' : 'No people';
                  const hasDates = isAfterToday(collection.startDate) || Boolean(collection.endDate);
                  return (
                    <ListItemFlexRow
                      key={i}
                      id={collection.collectionId}
                      disabled={!hasAccess}
                      onClick={hasAccess ? () => redirectToCollection(collection.collectionId) : undefined}
                      align="normal"
                    >
                      <CollectionImgContainer>
                        {collection.image ? (
                          <Image src={collection.image} alt={`${brand.name}: ${collection.name}`} />
                        ) : (
                          <CollectionImgPlaceholder />
                        )}
                      </CollectionImgContainer>
                      <CollectionInfo>
                        <h3 data-qa-hook="collectionName">{collection.name}</h3>
                        {hasDates && (
                          <InlineList content={'•'}>
                            {collection.startDate && isAfterToday(collection.startDate) && (
                              <li data-qa-hook="startsOn">
                                Starts {format(parseISO(collection.startDate), 'MMMM d, y')}
                              </li>
                            )}
                            {collection.endDate && (
                              <li data-qa-hook="endsOn">Ends {format(parseISO(collection.endDate), 'MMMM d, y')}</li>
                            )}
                          </InlineList>
                        )}
                        {hasAccess !== false && totalMembers >= 1 && (
                          <ProfileImageList srcUrls={group.profileImages} totalUsers={totalMembers} />
                        )}
                        {hasAccess ? (
                          <p data-qa-hook="collectionNumberOfMembers">{members}</p>
                        ) : (
                          <p>This collection is not included with your Basic subscription.</p>
                        )}
                      </CollectionInfo>
                    </ListItemFlexRow>
                  );
                })}
              </Collections>

              <AtHomeBanner brandCode={brand.code} ageCategory={ageCategory} brandName={brand.name} />
            </>
          )}
        </Container>
      </FullWidthCardContainer>
    </>
  );
}
